import moment from 'moment-timezone';

/**
 * Date-range primitives for the operations dashboards.
 *
 * Everything here is anchored in CENTRAL time on purpose: the servers bucket
 * by CT calendar days, so "This month" has to start on the same day the
 * backend thinks it does. Doing this math in the browser's zone would put a
 * user outside CT on the wrong day at the edges.
 *
 * The Lead Metrics Funnel's old DateRangeBar (with its own copy of these) was
 * retired 2026-10-25; the Funnel now uses this module.
 */

// Must match the server's window timezone (onboardingMetricsController CT /
// leadsDashboardController). If that ever becomes configurable, this becomes
// a value the API hands the client, not a constant.
export const CT = 'America/Chicago';

export const ymd = (m) => m.format('YYYY-MM-DD');

export const todayYmd = () => ymd(moment.tz(CT));

/**
 * Sunday-anchored week start, matching the Schedule calendar's grid so both
 * screens agree which days are "this week" (Nic 2026-08-14).
 *
 * Deliberately NOT moment's startOf('week'): that follows the active locale,
 * so a stray moment.locale() call anywhere in the bundle would silently move
 * every week boundary to Monday.
 */
export const startOfWeekYmd = (m) => ymd(m.clone().subtract(m.day(), 'days'));

/**
 * Prior PERIOD-to-date comparison for anchored presets: "This month" on the
 * 14th compares against the 1st-14th of LAST month, not against all of last
 * month, so a partial period never looks artificially down (Nic 2026-08-14).
 *
 * The end is clamped to the prior period's own end, which is what makes the
 * awkward cases behave: Mar 31 compares to Feb 28 (or 29), not to Mar 3.
 */
export const priorPeriodToDate = (unit) => (start, end) => {
  const s = moment.tz(start, 'YYYY-MM-DD', CT);
  const days = moment.tz(end, 'YYYY-MM-DD', CT).diff(s, 'days') + 1;
  const ps = s.clone().subtract(1, unit).startOf(unit);
  const pe = moment.min(
    ps.clone().add(days - 1, 'days'),
    ps.clone().endOf(unit),
  );
  return { prevStart: ymd(ps), prevEnd: ymd(pe) };
};

/**
 * The custom-range default: the equal-length stretch immediately before it.
 * Mirrors the server's own default so the client can label the comparison
 * before the response lands.
 */
export const precedingEqualLength = (start, end) => {
  const s = moment.tz(start, 'YYYY-MM-DD', CT);
  const days = moment.tz(end, 'YYYY-MM-DD', CT).diff(s, 'days') + 1;
  const pe = s.clone().subtract(1, 'day');
  return { prevStart: ymd(pe.clone().subtract(days - 1, 'days')), prevEnd: ymd(pe) };
};

const MONTHS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

/**
 * 'Jun 1 - Jun 30, 2026' from two YYYY-MM-DD strings. A single day renders
 * once ('Aug 14, 2026') rather than as a range to itself.
 * Parsed as strings, never as Dates: `new Date('2026-08-14')` is UTC midnight
 * and shifts a day backwards for anyone west of Greenwich.
 */
export const rangeLabel = (start, end) => {
  if (!start || !end) return '';
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  if (start === end) return `${MONTHS[sm - 1]} ${sd}, ${sy}`;
  const s = `${MONTHS[sm - 1]} ${sd}${sy !== ey ? `, ${sy}` : ''}`;
  return `${s} - ${MONTHS[em - 1]} ${ed}, ${ey}`;
};
